import { CancellationToken } from './cancellation'

/**
 * Interface that provides a stream download capability.
 */
export interface StreamDownloader {
  /**
   * Requests a download handle. May return `null` to abort.
   *
   * @param options
   */
  requestDownload(
    options: StreamDownloadOptions,
  ): Promise<DownloadHandle | null>
}

/**
 * A handle used to download the stream to a file.
 */
export interface DownloadHandle {
  /**
   * The file name.
   */
  readonly fileName: string
  /**
   * Performs the download.
   *
   * @param stream
   * @param cancellationToken
   */
  download(
    stream: ReadableStream<Uint8Array>,
    cancellationToken?: CancellationToken,
  ): Promise<void>
}

/**
 * Options for downloading a stream to the file system.
 * May or may not be honored depending on the implementation.
 */
export interface StreamDownloadOptions {
  /**
   * The file name to suggest.
   */
  suggestedName: string
  /**
   * The description of the type of file being downloaded
   */
  fileTypeDescription?: string
  /**
   * A map of the mime-type to extensions.
   */
  accept?: Record<string, string[]>
}

/**
 * Creates a stream downloader based on the current browser capabilities.
 */
export function createStreamDownloader(): StreamDownloader {
  if ('showSaveFilePicker' in window) {
    return createNativeStreamDownloader()
  }
  return createBlobStreamDownloader()
}

/**
 * Copies the stream contents to a `Blob`, then triggers a download for the
 * `Blob`. This is very inefficient as it requires copying the contents of the
 * stream *twice*.
 */
export function createBlobStreamDownloader(): StreamDownloader {
  return {
    async requestDownload(options) {
      return {
        fileName: options.suggestedName,
        async download(stream, cancellationToken = CancellationToken.default) {
          const parts: Uint8Array[] = []
          const writableStream = new WritableStream<Uint8Array>({
            write(chunk) {
              parts.push(chunk)
            },
          })
          await stream.pipeTo(writableStream, {
            signal: cancellationToken.asAbortSignal(),
          })
          const blob = new Blob(parts)
          const url = URL.createObjectURL(blob)
          const anchor = document.createElement('a')
          anchor.style.display = 'none'
          anchor.href = url
          anchor.download = options.suggestedName
          document.body.appendChild(anchor)
          anchor.click()
          URL.revokeObjectURL(url)
        },
      }
    },
  }
}

/**
 * Uses the `window.showSaveFilePicker` API to download the stream.
 */
export function createNativeStreamDownloader(): StreamDownloader {
  return {
    async requestDownload(options) {
      try {
        // This uses an experimental API that is supported in Chrome.
        // The previous approach of listing everything on the client
        // and then generating a CSV ended up hanging the browser past a few
        // thousand transactions.
        const fileSystemFileHandle = await window.showSaveFilePicker({
          suggestedName: options.suggestedName,
          types: options.accept
            ? [
                {
                  description: options.fileTypeDescription,
                  accept: options.accept,
                },
              ]
            : undefined,
        })

        return {
          fileName: fileSystemFileHandle.name,
          async download(
            stream,
            cancellationToken = CancellationToken.default,
          ): Promise<void> {
            // Create a writable stream from the file entry that was chosen.
            const writable = await fileSystemFileHandle.createWritable()
            await stream.pipeTo(writable, {
              signal: cancellationToken.asAbortSignal(),
            })
          },
        }
      } catch {
        // When the above throws, it is because the user canceled the file picker.
        return null
      }
    },
  }
}
